import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import supabase from '../../../config/supabaseClient';
import { 
  ArrowLeft, 
  Smartphone, 
  UserCheck,
  UserX,
  Mail, 
  Phone, 
  Calendar,
  MapPin,
  CreditCard,
  User,
  Image
} from 'lucide-react';
import toast from 'react-hot-toast';

const DetalhesUsuarioAplicativo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUsuario = async () => {
      try {
        const { data, error } = await supabase
          .from('usuario')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setUsuario(data);
      } catch (error) {
        console.error('Erro ao carregar usuário:', error);
        toast.error('Erro ao carregar usuário');
      } finally {
        setLoading(false);
      }
    };

    loadUsuario();
  }, [id]);

  const toggleStatusUsuario = async (ativo) => {
    try {
      const { error } = await supabase
        .from('usuario')
        .update({ ativo })
        .eq('id', id);

      if (error) throw error;

      toast.success(`Usuário ${ativo ? 'ativado' : 'desativado'} com sucesso!`);
      setUsuario(prev => ({ ...prev, ativo }));
    } catch (error) {
      console.error('Erro ao alterar status do usuário:', error);
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const handleToggleStatus = async () => {
    if (window.confirm(`Tem certeza que deseja ${usuario.ativo ? 'desativar' : 'ativar'} este usuário?`)) {
      await toggleStatusUsuario(!usuario.ativo);
    }
  };

  if (loading) {
    return (
      <div className="gestao-usuarios detalhes-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando detalhes do usuário...</p>
        </div>
      </div>
    );
  }

  if (!usuario) {
    return (
      <div className="gestao-usuarios detalhes-container">
        <div className="empty-state">
          <Smartphone size={48} />
          <h3>Usuário não encontrado</h3>
          <p>O usuário solicitado não foi encontrado.</p>
          <Link to="/gestao-usuarios/populacao" className="button primary">
            Voltar à Lista
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="gestao-usuarios detalhes-container">
      <div className="detalhes-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button 
            className="back-button"
            onClick={() => navigate('/gestao-usuarios/populacao')}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="header-content">
            <h1>
              <Smartphone size={32} />
              Detalhes do Usuário do Aplicativo
            </h1>
            <p>Informações completas do usuário {usuario.nome || 'Sem nome'}</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            onClick={handleToggleStatus}
            className={`button ${usuario.ativo ? 'danger' : 'success'}`}
          >
            {usuario.ativo ? (
              <>
                <UserX size={16} />
                Desativar
              </>
            ) : (
              <>
                <UserCheck size={16} />
                Ativar
              </>
            )}
          </button>
        </div>
      </div>

      <div className="detalhes-content">
        <div className="detalhes-grid">
          {/* Informações Básicas */}
          <div className="detalhes-section">
            <h3>Informações Básicas</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Nome Completo</label>
                <div className="info-value">
                  <User size={16} />
                  {usuario.nome || 'Não informado'}
                </div>
              </div>
              
              {usuario.email && (
                <div className="info-item">
                  <label>Email</label>
                  <div className="info-value">
                    <Mail size={16} />
                    {usuario.email}
                  </div>
                </div>
              )}
              
              {usuario.celular && (
                <div className="info-item">
                  <label>Telefone</label>
                  <div className="info-value">
                    <Phone size={16} />
                    {usuario.celular}
                  </div>
                </div>
              )}
              
              <div className="info-item">
                <label>Status</label>
                <div className="info-value">
                  <span className={`status-badge ${usuario.ativo ? 'status-ativo' : 'status-inativo'}`}>
                    {usuario.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Documentos e Endereço */}
          <div className="detalhes-section">
            <h3>Documentos e Endereço</h3>
            <div className="info-grid">
              {usuario.cpf && (
                <div className="info-item">
                  <label>CPF</label>
                  <div className="info-value">
                    <CreditCard size={16} />
                    {usuario.cpf}
                  </div>
                </div>
              )}
              
              {usuario.endereco && (
                <div className="info-item full-width">
                  <label>Endereço</label>
                  <div className="info-value">
                    <MapPin size={16} />
                    {usuario.endereco}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Foto do Usuário */}
          {usuario.foto && (
            <div className="detalhes-section">
              <h3>Foto do Usuário</h3>
              <div className="user-photo-container">
                <img 
                  src={usuario.foto} 
                  alt="Foto do usuário" 
                  className="user-photo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="photo-placeholder" style={{ display: 'none' }}>
                  <Image size={48} />
                  <p>Erro ao carregar foto</p>
                </div>
              </div>
            </div>
          )}

          {/* Informações de Sistema */}
          <div className="detalhes-section full-width">
            <h3>Informações do Sistema</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Cadastrado em</label>
                <div className="info-value">
                  <Calendar size={16} />
                  {new Date(usuario.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
              
              <div className="info-item">
                <label>ID do Usuário</label>
                <div className="info-value">
                  <User size={16} />
                  {usuario.id}
                </div>
              </div>

              {usuario.uid && (
                <div className="info-item">
                  <label>UID de Autenticação</label>
                  <div className="info-value">
                    <User size={16} />
                    {usuario.uid}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetalhesUsuarioAplicativo;